import React, { useCallback, useState } from 'react';
import {
  Dialog,
  Button,
  TextField,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import {
  GetAllTeamsForUserManagementDocument,
  useCreateTeamMutation,
} from '@/__generated__/graphql';
import { containedButtonStyle, textButtonStyle } from '@/theme';

type Props = {
  open: boolean;
  handleToggle: () => void;
};

export const AddTeamDialog = ({ open, handleToggle }: Props) => {
  const [teamName, setTeamName] = useState('');
  const [error, setError] = useState<boolean>(false);
  const [createTeam, { error: createTeamError }] = useCreateTeamMutation();

  const handleTeamNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (error) setError(false);
      setTeamName(event.target.value);
    },
    [error]
  );

  const handleClose = useCallback(() => {
    setTeamName('');
    handleToggle();
  }, [handleToggle]);

  const handleCreateTeam = async () => {
    try {
      await createTeam({
        variables: {
          name: teamName.trim(),
        },
        refetchQueries: [GetAllTeamsForUserManagementDocument],
      });

      handleClose();
    } catch {
      setError(true);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          minWidth: '45rem',
        },
      }}
    >
      <DialogTitle>Create Team</DialogTitle>
      <DialogContent>
        <TextField
          data-testid="team-name-input"
          label="Team Name"
          variant="standard"
          sx={{ width: '100%', mb: 2 }}
          value={teamName}
          error={error}
          helperText={error ? createTeamError?.message : ' '}
          onChange={handleTeamNameChange}
        />
      </DialogContent>
      <Divider orientation="horizontal" />
      <DialogActions sx={{ pt: 2, pb: 2 }}>
        <Button
          onClick={handleClose}
          variant="text"
          sx={textButtonStyle}
          data-testid="cancel-button"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={containedButtonStyle}
          onClick={handleCreateTeam}
          disabled={!teamName || error}
          data-testid="create-team-button"
        >
          Create Team
        </Button>
      </DialogActions>
    </Dialog>
  );
};
